import React, { useState, useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { useSelector } from "react-redux";
import styles from "./ExamWisePerformance.module.css";
import { ClipboardCheck } from "react-bootstrap-icons";

ChartJS.register(ArcElement, ChartTooltip, Legend);

/* Read a CSS custom-property from :root */
const readVar = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v || fallback;
};

/* Pass colors per exam (Fail is always grey, like the screenshot) */
const PASS_COLORS = [
  readVar("--pink-dark", "#ec4899"),
  readVar("--lime-light", "#d9ed92"),
  readVar("--blue-dark", "#2563eb"),
  readVar("--violet-light", "#b197fc"),
  readVar("--teal-dark", "#166a8f"),
  readVar("--orange-light", "#fdba74"),
];
const FAIL_COLOR = "#b0b3bd"; // grey

const passColor = (i) => PASS_COLORS[i % PASS_COLORS.length];

/* ============================================================================
 * Drill-down stacked-bar tooltip (recharts) — unchanged
 * ==========================================================================*/
const ChartCustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const passVal = payload.find((p) => p.dataKey === "pass")?.value || 0;
    const failVal = payload.find((p) => p.dataKey === "fail")?.value || 0;
    return (
      <div className={styles.customTooltip}>
        <p className={styles.tooltipTitle}>{payload[0].payload.subject}</p>
        <p className={styles.tooltipRow} style={{ color: "#3cd49b" }}>
          ● Pass: <strong>{Math.round(passVal)}%</strong>
        </p>
        <p className={styles.tooltipRow} style={{ color: "#f66060" }}>
          ● Fail: <strong>{Math.round(failVal)}%</strong>
        </p>
      </div>
    );
  }
  return null;
};

/* ============================================================================
 * SUB-COMPONENT: ExamSubjectBreakdown (100% Stacked Bar View) — unchanged
 * ==========================================================================*/
const ExamSubjectBreakdown = ({ examName, data, onBack }) => {
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations?.labels]);

  if (!data || data.length === 0)
    return <div className={styles.centerMsg}>No subject data available.</div>;

  const renderCustomLabel = (props) => {
    const { x, y, width, height, value } = props;
    if (!value) return null;
    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        fill="#ffffff"
        textAnchor="middle"
        dominantBaseline="middle"
        style={{ fontSize: "11px", fontWeight: 700 }}
      >
        {`${Math.round(value)}%`}
      </text>
    );
  };

  return (
    <div className={styles.cardContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.icon}>
            <ClipboardCheck  size={18} />

          </span>
          <h2 className={styles.title}>
            {t.examwisePerformance || "Exam wise Performance"} - {examName}
          </h2>
        </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onBack}
          aria-label="Back to main chart"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={data} margin={{ top: 20, right: 5, left: -25, bottom: 5 }} barSize={36}>
            <CartesianGrid strokeDasharray="0" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="subject" tickLine={false} axisLine={{ stroke: "#f0f0f0" }} tick={{ fill: "#7a829a", fontSize: 11 }} />
            <YAxis domain={[0, 100]} tickLine={false} axisLine={{ stroke: "#f0f0f0" }} tick={{ fill: "#7a829a", fontSize: 12 }} />
            <Tooltip content={<ChartCustomTooltip />} cursor={{ fill: "#f4f6fa", opacity: 0.6 }} />
            <Bar dataKey="pass" stackId="a" fill="#3cd49b">
              <LabelList dataKey="pass" content={renderCustomLabel} />
            </Bar>
            <Bar dataKey="fail" stackId="a" fill="#f66060">
              <LabelList dataKey="fail" content={renderCustomLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className={styles.barLegendRow}>
        <div className={styles.legendItem}>
          <span className={styles.colorDot} style={{ backgroundColor: "#3cd49b" }} />
          <span className={styles.legendText}>{examName} - Pass</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.colorDot} style={{ backgroundColor: "#f66060" }} />
          <span className={styles.legendText}>{examName} - Fail</span>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
 * MAIN EXPORT: ExamPerformanceChart (multi-series pie overview)
 * ==========================================================================*/
const ExamPerformanceChart = ({ data, loading, error }) => {
  const [selectedExamView, setSelectedExamView] = useState(null);
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations?.labels]);

  const exams = data || [];

  /* ── Build pie data (hooks first — must run every render) ──
   * One dataset (ring) per exam. Chart.js draws dataset[0] innermost,
   * so reverse to put the first exam on the OUTER ring (screenshot order).
   * Two slices per ring: [Pass, Fail]. Two labels per dataset for the
   * multi-series legend math. */
  const pieData = useMemo(() => {
    const ordered = exams.slice().reverse();
    const labels = [];
    const datasets = ordered.map((item) => {
      const i = item.colorIndex ?? exams.indexOf(item);
      const pass = Number(item.passPercentage ?? 0);
      const fail = Math.max(0, 100 - pass);
      labels.push(`${item.exam} - Fail`, `${item.exam} - Pass`);   // ← order swapped
      return {
        label: item.exam,
        data: [fail, pass],                                        // ← Fail first, then Pass
        backgroundColor: [FAIL_COLOR, passColor(i)],               // ← match new order
        borderColor: "#ffffff",
        borderWidth: 2,
        _examName: item.exam,
      };
    });
    return { labels, datasets };
  }, [exams]);

  const pieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      // Ring slice click → drill into that exam's subjects
      onClick: (_evt, elements) => {
        if (!elements?.length) return;
        const dsIndex = elements[0].datasetIndex;
        const examName = pieData.datasets[dsIndex]?._examName;
        if (examName) setSelectedExamView(examName);
      },
      onHover: (evt, elements) => {
        const c = evt?.native?.target;
        if (c) c.style.cursor = elements.length ? "pointer" : "default";
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            pointStyle: "rect",
            padding: 12,
            font: { size: 12 },
            generateLabels(chart) {
              const original =
                ChartJS.overrides.pie.plugins.legend.labels.generateLabels;
              const labels = original.call(this, chart);
              const datasetColors = chart.data.datasets
                .map((d) => d.backgroundColor)
                .flat();
              labels.forEach((label) => {
                // 2 labels per dataset → dataset index
                label.datasetIndex = (label.index - (label.index % 2)) / 2;
                label.hidden = !chart.isDatasetVisible(label.datasetIndex);
                label.fillStyle = datasetColors[label.index];
                label.strokeStyle = datasetColors[label.index];
              });
              return labels;
            },
          },
          // Legend click → toggle ring visibility (strikeout), NOT drill-down
          onClick(_e, legendItem, legend) {
            const ci = legend.chart;
            ci.getDatasetMeta(legendItem.datasetIndex).hidden =
              ci.isDatasetVisible(legendItem.datasetIndex);
            ci.update();
          },
        },
        tooltip: {
          callbacks: {
            title(ctx) {
              const ds = pieData.datasets[ctx[0].datasetIndex];
              const slice = ctx[0].dataIndex === 0 ? "Fail" : "Pass";   // ← was "Pass" : "Fail"
              return `${ds?._examName} - ${slice}`;
            },
            label(ctx) {
              return ` ${ctx.formattedValue}%`;
            },
          },
        },
      },
    }),
    [pieData],
  );

  /* ── Drill-down view (early return AFTER all hooks) ── */
  if (selectedExamView) {
    const matchedExam = data?.find((item) => item.exam === selectedExamView);
    const formattedSubjectData =
      matchedExam?.subjects?.map((sub) => ({
        subject: sub.subject_name,
        pass: Number(sub.pass_percentage),
        fail: Number(sub.fail_percentage),
      })) || [];

    return (
      <ExamSubjectBreakdown
        examName={selectedExamView}
        data={formattedSubjectData}
        onBack={() => setSelectedExamView(null)}
      />
    );
  }

  /* ── State flags ── */
  const isLoading = loading;
  const isError = !!error && !loading;
  const isEmpty = !loading && !error && (!data || data.length === 0);

  return (
    <div className={styles.cardContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.icon}>
            <ClipboardCheck size={20} />

          </span>
          <h2 className={styles.title}>
            {t.examwisePerformance || "Exam wise Performance"}
          </h2>
        </div>
      </div>

      {isLoading && <div className={styles.centerMsg}>Loading exam analytics...</div>}
      {isError && <div className={styles.centerMsg}>{error}</div>}
      {isEmpty && <div className={styles.centerMsg}>No data available.</div>}

      {!isLoading && !isError && !isEmpty && (
        <div className={styles.chartWrapper} style={{ height: 420 }}>
          <Pie data={pieData} options={pieOptions} />
        </div>
      )}
    </div>
  );
};

export default ExamPerformanceChart;